import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { generateCodeChallenge, generateCodeVerifier } from "./FHIRHelper";
import purpleLogo from "../../assets/purpleKnowtexLogo.svg";
import { markStandaloneUser } from "../../utils/standaloneUser";

const STANDALONE_SCOPE =
  process.env.REACT_APP_STANDALONE_OIDC_SCOPE || "openid profile email offline_access";

const createRandomState = () => {
  const random = new Uint8Array(24);
  window.crypto.getRandomValues(random);
  return Array.from(random, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export default function StandaloneLanding() {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState("");

  const config = useMemo(
    () => ({
      clientId: process.env.REACT_APP_STANDALONE_OIDC_CLIENT_ID,
      authorizationEndpoint:
        process.env.REACT_APP_STANDALONE_OIDC_AUTHORIZATION_ENDPOINT,
    }),
    [],
  );

  const handleStandaloneSignIn = async () => {
    if (!config.clientId || !config.authorizationEndpoint) {
      setError("Standalone OIDC configuration is missing.");
      return;
    }

    // Reset stale standalone marker before beginning a new auth handshake.
    markStandaloneUser(false);
    setError("");
    setIsRedirecting(true);

    try {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = createRandomState();
      const nonce = createRandomState();
      const redirectUri = `${window.location.origin}/auth/standalone`;

      localStorage.setItem("standalone_oidc_state", state);
      localStorage.setItem("standalone_oidc_nonce", nonce);
      localStorage.setItem("standalone_oidc_code_verifier", codeVerifier);

      const authorizationUrl = new URL(config.authorizationEndpoint);
      authorizationUrl.searchParams.append("client_id", config.clientId);
      authorizationUrl.searchParams.append("response_type", "code");
      authorizationUrl.searchParams.append("redirect_uri", redirectUri);
      authorizationUrl.searchParams.append("scope", STANDALONE_SCOPE);
      authorizationUrl.searchParams.append("state", state);
      authorizationUrl.searchParams.append("nonce", nonce);
      authorizationUrl.searchParams.append("code_challenge", codeChallenge);
      authorizationUrl.searchParams.append("code_challenge_method", "S256");
      authorizationUrl.searchParams.append("response_mode", "query");

      window.location.assign(authorizationUrl.toString());
    } catch (err) {
      console.error("Standalone OIDC launch failed:", err);
      setError("Unable to start standalone sign-in.");
      setIsRedirecting(false);
    }
  };

  return (
    // Scrolls instead of clipping so the sign-in button stays reachable at 400% zoom.
    <main
      id="main-content"
      tabIndex={-1}
      className="relative w-dvw h-dvh flex flex-col items-center px-4 py-8 overflow-x-hidden overflow-y-auto focus:outline-none"
    >
      <Helmet><title>Sign In – Knowtex</title></Helmet>
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-left bg-auto bg-login-background -scale-x-100"
      />
      <img
        src={purpleLogo}
        alt="Knowtex"
        className="relative z-10 min-w-[16rem] max-w-[34rem] w-[32%] min-h-[8rem] h-[32%] my-2"
      />

      <div className="relative z-10 w-full max-w-[36rem] bg-white rounded-xl shadow-drop-shadow px-8 py-10 md:px-12 mt-[-1rem]">
        <h1 className="text-3xl text-ktx-navy font-semibold mb-3 text-center">
          Sign in to Knowtex
        </h1>
        <p className="text-knowtex-grey text-center mb-8">
          Use your organization Microsoft account to continue.
        </p>

        <div role="alert">
          {error && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 text-orange-700 px-4 py-3 mb-6 text-sm text-center">
              {error}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleStandaloneSignIn}
          disabled={isRedirecting}
          className={`w-full py-3 rounded-md text-white font-semibold tracking-wide transition-colors flex items-center justify-center gap-3 ${
            isRedirecting
              ? "bg-accent2/70 cursor-not-allowed"
              : "bg-accent2 hover:bg-accent1"
          }`}
        >
          {!isRedirecting && (
            <svg
              aria-hidden="true"
              className="h-4 w-4"
              viewBox="0 0 23 23"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fill="#f25022" d="M1 1h10v10H1z" />
              <path fill="#7fba00" d="M12 1h10v10H12z" />
              <path fill="#00a4ef" d="M1 12h10v10H1z" />
              <path fill="#ffb900" d="M12 12h10v10H12z" />
            </svg>
          )}
          {isRedirecting ? "Redirecting..." : "Continue with Microsoft"}
        </button>

        <p className="text-xs text-knowtex-grey text-center mt-4">
          You will be redirected to Microsoft to authenticate.
        </p>
      </div>
    </main>
  );
}
